import { ColumnType } from 'antd/lib/table';
import React from 'react';
import { TableColumn } from './interfaces';
import { FilterFilled } from '@ant-design/icons';
import { FetchGridParameters, GridParameterFilter } from '../../Networking';
import { FilterStateWrapper } from './FilterStateWrapper';

export type FieldType = 'string' | 'number' | 'boolean' | 'select';

//regex from https://stackoverflow.com/questions/18379254/regex-to-split-camel-case
function spaceAndTitleCase(phrase: string) {
  return phrase
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function getColumns<DataType>(
  torqColumns: TableColumn<DataType>[],
  params: FetchGridParameters<DataType> | undefined,
  onFilter: (filters?: FetchGridParameters<DataType>['filters']) => void
) {
  const orderKey = params?.orderKey;
  const order = params?.order ?? 'asc';
  const filters =
    params?.filters ?? ({} as FetchGridParameters<DataType>['filters'])!;

  return torqColumns
    .map((c) => {
      if (!c.field)
        return buildColumn(c, c.field == orderKey ? order : undefined);

      const filter = filters ? filters[c.field] : undefined;

      const onChangeFilter = (newColumnFilters?: GridParameterFilter[]) => {
        if (!c.field)
          throw Error(
            "Should we be in this method if a field wasn't defined for this column? Should we even have filters in such a case?!"
          );

        let newFilters: FetchGridParameters<DataType>['filters'];

        if (newColumnFilters) {
          newFilters = {
            ...filters,
            [c.field]: newColumnFilters,
          } as FetchGridParameters<DataType>['filters'];
        } else {
          newFilters = {
            ...filters,
          } as FetchGridParameters<DataType>['filters'];

          if (newFilters![c.field]) {
            delete newFilters![c.field];
          }
        }

        onFilter(newFilters);
      };

      return buildColumn(
        c,
        c.field == orderKey ? order : undefined,
        filter && c.field ? filters[c.field] : undefined,
        onChangeFilter
      );
    })
    .filter(notEmpty);
}

function buildColumn<DataType>(
  columnData: TableColumn<DataType>,
  sortOrder: 'asc' | 'desc' | undefined,
  filter?: GridParameterFilter[] | undefined,
  onFilter?: (filter?: GridParameterFilter[]) => void
): ColumnType<DataType> {
  const {
    field,
    filter: columnFilter,
    key,
    title,
    sortable,
    width,
    render,
  } = columnData;

  const onFilterSet = (f?: GridParameterFilter[], closeFilter?: () => void) => {
    onFilter && onFilter(f);

    if (closeFilter) {
      closeFilter();
    }
  };

  const filterIcon =
    columnData.filter != null ? (
      <FilterFilled
        style={{
          color: filter ? 'var(--torq-experimental-primary-color)' : undefined,
        }}
      />
    ) : undefined;

  var column: ColumnType<DataType> = {
    title: title ?? spaceAndTitleCase(field as string),
    dataIndex: field as string,
    key: key,
    width,
    render,

    sorter: sortable,
    sortOrder: sortOrder
      ? sortOrder == 'asc'
        ? 'ascend'
        : 'descend'
      : undefined,

    filterDropdown:
      columnFilter && field
        ? ({ visible, clearFilters }) => (
            <FilterStateWrapper
              columnFilter={columnFilter}
              //
              //We may not be using ant's internal filter state anymore but
              // we can still certainly make use of their state that manages
              // which filter is visible! We just use clearFilters as a ghetto
              // close filter method which is completely harmless since
              // we're not using ant's filter system anyway.
              setFilter={(f) => onFilterSet(f, clearFilters)}
              visible={visible}
              filter={filter}
            />
          )
        : undefined,
    filterIcon,
  };
  return column;
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}
